// Reports → Passes & wallet (F-ANL-03; spec/17 M-04, M-19): what went into wallets (a liability, not revenue), what
// was spent from them (revenue, on the day it is spent), the bonus credit spent (a promotional cost, never revenue), and
// what the organisation owes players now — wallet balances and unused pass time, as a donut — and is owed on account.

import type { RevenueReport } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { BarChart, ChartEmpty, ChartTable, DonutChart, LineChart, StatCard } from '@clavian/ui-web';
import type { ReactNode } from 'react';

import { trendSummary, withDelta } from './OverviewTab';
import { KpiFigure, ReportCard, ReportGrid, ReportKpis, ReportNote, ReportPage, ReportRow, ReportRows } from './report-kit';
import { type ReportScope, useRevenueReport } from './queries';
import { dayLong, dayShort, deltaOf, moneyOrDash, moneyShort, moneyTick, shareWords, sparkOf, TabPending } from './tab-kit';

export function WalletTab({ scope, previousLabel }: { readonly scope: ReportScope; readonly previousLabel: string }): ReactNode {
  const { t } = useTranslation('console');
  const report = useRevenueReport(scope, true);
  if (report.data === undefined) return <TabPending query={report} unavailable={t('reports.unavailable')} forbidden={t('reports.notPermittedFinancial')} />;
  return <Wallet report={report.data} previousLabel={previousLabel} />;
}

function Wallet({ report, previousLabel }: { readonly report: RevenueReport; readonly previousLabel: string }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const short = (amount: number) => moneyShort(format, amount, report.currency);
  const caption = t('reports.vsPrevious', { range: previousLabel });
  const wallet = report.wallet;
  const owed = wallet.outstandingCash === null && wallet.outstandingBonus === null ? null : (wallet.outstandingCash?.amount ?? 0) + (wallet.outstandingBonus?.amount ?? 0);
  const flows = [
    { key: 'topUps', label: t('reports.wallet.topUps'), meta: t('reports.wallet.notRevenue'), money: wallet.topUps, tone: 1 as const },
    { key: 'spent', label: t('reports.wallet.spent'), meta: t('reports.wallet.isRevenue'), money: wallet.spent, tone: 1 as const },
    { key: 'handedBack', label: t('reports.wallet.handedBack'), meta: t('reports.wallet.handedBackHint'), money: wallet.handedBack, tone: 5 as const },
    { key: 'bonusGranted', label: t('reports.wallet.bonusGranted'), meta: t('reports.wallet.bonusGrantedHint'), money: wallet.bonusGranted, tone: 2 as const },
    { key: 'bonusSpent', label: t('reports.wallet.bonusSpent'), meta: t('reports.wallet.promoCost'), money: wallet.bonusSpent, tone: 2 as const },
  ];
  const moved = flows.filter((flow) => flow.money.amount !== 0);
  const owedSlices = [
    { key: 'cash', label: t('reports.wallet.walletCash'), money: wallet.outstandingCash, tone: 1 as const },
    { key: 'bonus', label: t('reports.wallet.walletBonus'), money: wallet.outstandingBonus, tone: 2 as const },
    { key: 'passes', label: t('reports.wallet.passLiability'), money: wallet.passLiability, tone: 3 as const, hint: t('reports.wallet.passLiabilityHint') },
  ];
  const owedTotal = owedSlices.reduce((sum, slice) => sum + Math.max(0, slice.money?.amount ?? 0), 0);

  return (
    <ReportPage>
      <ReportKpis label={t('reports.tab.wallet')}>
        <StatCard
          label={t('reports.wallet.topUps')}
          value={<KpiFigure value={wallet.topUps.amount} format={short} />}
          {...withDelta(deltaOf(wallet.topUps.amount, report.previous?.walletTopUps.amount, caption, format))}
          hint={t('reports.wallet.topUpsHint')}
          {...sparkOf(report.byDay.map((day) => day.walletTopUps.amount))}
        />
        <StatCard
          label={t('reports.wallet.spent')}
          value={<KpiFigure value={wallet.spent.amount} format={short} />}
          {...withDelta(deltaOf(wallet.spent.amount, report.previous?.walletSpent.amount, caption, format))}
          hint={t('reports.wallet.spentHint')}
          {...sparkOf(report.byDay.map((day) => day.walletSpent.amount))}
        />
        <StatCard
          label={t('reports.wallet.bonusSpent')}
          value={<KpiFigure value={wallet.bonusSpent.amount} format={short} />}
          {...withDelta(deltaOf(wallet.bonusSpent.amount, report.previous?.bonusSpent.amount, caption, format, false))}
          hint={t('reports.wallet.bonusSpentHint', { granted: format.money(wallet.bonusGranted) })}
          {...sparkOf(report.byDay.map((day) => day.bonusSpent.amount))}
        />
        <StatCard label={t('reports.wallet.owed')} value={<KpiFigure value={owed} format={short} />} hint={t('reports.wallet.owedHint')} />
      </ReportKpis>

      <ReportCard title={t('reports.wallet.trend')} description={t('reports.wallet.trendHint')}>
        <LineChart
          label={t('reports.wallet.trend')}
          summary={trendSummary(t, report.byDay.map((day) => day.walletSpent.amount), (index) => dayLong(format, report.byDay[index]?.date ?? ''), short)}
          categories={report.byDay.map((day) => dayShort(format, day.date))}
          categoryLabel={(index) => dayLong(format, report.byDay[index]?.date ?? '')}
          series={[
            { key: 'spent', label: t('reports.wallet.spent'), tone: 1, area: true, values: report.byDay.map((day) => day.walletSpent.amount) },
            { key: 'topUps', label: t('reports.wallet.topUps'), tone: 5, values: report.byDay.map((day) => day.walletTopUps.amount) },
          ]}
          format={short}
          formatTick={(value) => moneyTick(format, value, report.currency)}
        />
        <ChartTable
          caption={t('reports.wallet.trend')}
          rows={report.byDay}
          rowKey={(row) => row.date}
          columns={[
            { key: 'date', header: t('reports.column.day'), cell: (row) => dayLong(format, row.date) },
            { key: 'topUps', header: t('reports.wallet.topUps'), numeric: true, cell: (row) => <span className="console-money">{format.money(row.walletTopUps)}</span> },
            { key: 'spent', header: t('reports.wallet.spent'), numeric: true, cell: (row) => <span className="console-money">{format.money(row.walletSpent)}</span> },
            { key: 'bonus', header: t('reports.wallet.bonusSpent'), numeric: true, cell: (row) => <span className="console-money">{format.money(row.bonusSpent)}</span> },
          ]}
        />
      </ReportCard>

      <ReportGrid layout="even">
        <ReportCard title={t('reports.wallet.flows')} description={t('reports.wallet.flowsHint')}>
          {moved.length === 0 ? (
            <ChartEmpty>{t('reports.wallet.noFlows')}</ChartEmpty>
          ) : (
            <BarChart
              label={t('reports.wallet.flows')}
              rows={moved.map((flow) => ({ key: flow.key, label: flow.label, meta: flow.meta, value: Math.abs(flow.money.amount), display: format.money(flow.money), tone: flow.tone }))}
            />
          )}
        </ReportCard>
        <ReportCard title={t('reports.wallet.liabilities')} description={t('reports.wallet.liabilitiesHint')}>
          {owedTotal === 0 ? (
            <ChartEmpty>{t('reports.wallet.nothingOwed')}</ChartEmpty>
          ) : (
            <DonutChart
              label={t('reports.wallet.liabilities')}
              slices={owedSlices.map((slice) => ({
                key: slice.key,
                label: slice.label,
                value: slice.money?.amount ?? 0,
                display: moneyOrDash(format, slice.money),
                tone: slice.tone,
                ...(slice.hint === undefined ? {} : { hint: slice.hint }),
              }))}
              total={{ display: short(owedTotal), label: t('reports.wallet.owedTotal') }}
              percent={(share) => shareWords(format, share)}
            />
          )}
          <ReportRows>
            <ReportRow label={t('reports.wallet.receivables')} hint={t('reports.wallet.receivablesHint')} value={moneyOrDash(format, wallet.receivablesOutstanding)} quiet={wallet.receivablesOutstanding === null} />
          </ReportRows>
          <ReportNote>{t('reports.wallet.organisationWide')}</ReportNote>
        </ReportCard>
      </ReportGrid>
    </ReportPage>
  );
}
